import {Controller} from "stimulus";
import Glide from "@glidejs/glide";
import {useDispatch} from 'stimulus-use';
import {getCookie} from "../../init/cookie";
import unitQuote from "util/quote";

export default class extends Controller {
    static targets = [
        "rateName", "ratePrice", "minRate",
        "bookBtn", "availabilityBtn",
        "glide", "bulletsContainer"
    ];

    connect() {
        useDispatch(this);
        this.unitType = this.data.get("unit-type");
        this.unitId = this.data.get("unit-id");
        this.unitTypeId = this.data.get("unit-type-id");
        this.checkoutPath = this.data.get("checkout-path");
        this.villaPath = this.data.get("villa-path");
        this.ratesPresent = false;
        this.checkin = this.data.get("checkin");
        this.checkout = this.data.get("checkout");
        this.minRateBackup = this.data.get("min-rate");
        this.cicleLeadTime = this.data.get("cicle-lead-time");
        this.coupon = this.data.get("coupon");
        this.setupGlideJS();
        this.setGuestsVariables();
        this.updateBookButton();
        this.observeVisibility();
    }

    setupGlideJS() {
        const slides = this.glideTarget.querySelector(".glide__slides");

        if (slides.children.length === 0) {
            return;
        }

        this.glide = new Glide(this.glideTarget, {
            type: "carousel",
            perView: 1,
            gap: 20,
            swipeThreshold: 5,
            dragThreshold: 5,
            animationDuration: 5
        }).mount();

        const bullets = this.glideTarget.querySelectorAll('.glide__bullet');
        const bulletsContainer = this.glideTarget.querySelector('.glide__bullets');

        if (bullets[4]) bullets[4].classList.add('glide__bullet--right');

        this.glide.on("run.after", () => {
            const activeBullet = this.glideTarget.querySelector('.glide__bullet--active');

            if (activeBullet) {
                const containerWidth = bulletsContainer.clientWidth;
                const bulletOffsetLeft = activeBullet.offsetLeft;
                const bulletWidth = activeBullet.clientWidth;

                const scrollPosition = bulletOffsetLeft - (containerWidth / 2) + (bulletWidth / 2);

                bulletsContainer.scrollTo({
                    left: scrollPosition,
                    behavior: 'smooth'
                });

                this.updateDots();
            }
        });
    }

    updateDots() {
        const bullets = this.glideTarget.querySelectorAll('.glide__bullet');
        const activeIndex = Array.from(bullets).findIndex(bullet => bullet.classList.contains('glide__bullet--active'));

        bullets.forEach(bullet => {
            bullet.classList.remove('glide__bullet--left', 'glide__bullet--right');
        });

        if (activeIndex === 1) {
            bullets[activeIndex + 3].classList.add('glide__bullet--right');
            bullets[activeIndex - 1].classList.add('glide__bullet--left');
            return
        }

        if (activeIndex === bullets.length - 2) {
            bullets[bullets.length - 1].classList.add('glide__bullet--right');
            bullets[bullets.length - 5].classList.add('glide__bullet--left');
            return
        }

        if (activeIndex === bullets.length - 1) {
            bullets[bullets.length - 5].classList.add('glide__bullet--left');
            return
        }

        if (activeIndex === 0) {
            bullets[activeIndex + 4].classList.add('glide__bullet--right');
            return
        }
        if (activeIndex - 2 >= 0 && activeIndex !== bullets.length - 1) {
            bullets[activeIndex - 2].classList.add('glide__bullet--left');
        }

        if (activeIndex + 2 < bullets.length) {
            bullets[activeIndex + 2].classList.add('glide__bullet--right');
        }
    }

    setGuestsVariables() {
        const urlParams = new URLSearchParams(window.location.search);
        this.adults = urlParams.get("adults") || 0;
        this.children = urlParams.get("children") || 0;

        if (this.adults || this.children) return;

        const cookies = JSON.parse(getCookie("filter_by_guests") || "{}");
        this.adults = Number(cookies.adults) || 0;
        this.children = Number(cookies.children) || 0;
    }

    bookingItemParams() {
        return {
            unit_id: this.unitId,
            type: this.unitType,
            unit_type_id: this.unitTypeId,
            arrival_date: this.checkin,
            departure_date: this.checkout,
            adults: this.adults,
            children: this.children,
            total: this.total
        };
    }

    updateBookButton() {
        if (!this.checkin || !this.checkout) {
            this.bookBtnTarget.textContent = 'Choose Dates';
            return;
        }

        this.bookBtnTarget.removeAttribute("disabled");
        this.bookBtnTarget.classList.remove("disabled");
        if (this.hasAvailabilityBtnTarget) {
            this.availabilityBtnTarget.classList.remove("disabled");
        }
    }

    async handleBookNowBtnClick(ev) {
        if (!this.checkin && !this.checkout) {
            this.handleMissingDates();
            return;
        }

        if (this.ratesPresent) {
            // noop
        } else {
            if (this.unitType === 'unit' || this.unitType === 'unit_type') {
                this.setCurrentEvent(ev);
            }
            await this.sendQuoteRequest();
        }
    }

    setCurrentEvent(e) {
        this.currentEvent = e;
    }

    resetCurrentEvent() {
        if (this.currentEvent) {
            this.currentEvent = null;
        }
    }

    handleMissingDates() {
        this.dispatch('searchbarFilterClicked', {type: 'dates'});
    }

    async sendQuoteRequest() {
        if (!this.checkin || !this.checkout) {
            this.resetCurrentEvent();
            return;
        }

        this.bookBtnTarget.classList.add('disabled');
        this.bookBtnTarget.textContent = "Checking ...";

        const data = {
            unit_id: this.unitId,
            type: this.unitType,
            arrival_date: this.checkin,
            departure_date: this.checkout
        };

        if (this.coupon) data.promo_code = this.coupon;

        const result = await unitQuote(data);
        await this.handleQuoteResponse(result);
    }

    disableBookButton() {
        if (this.bookBtnTarget) {
            this.bookBtnTarget.setAttribute("disabled", true);
            this.bookBtnTarget.classList.add('disabled');
            this.bookBtnTarget.textContent = "Unavailable";
        }
        // The unit isn't bookable for the searched dates — hide the whole card
        // so the listing only shows available homes.
        this.element.style.display = 'none';
    }

    async handleQuoteResponse(response) {
        if (response.status && response.status === 'error') {
            if (response.transient) {
                this.handleTransientQuote();
            } else {
                this.disableBookButton();
            }
        } else {
            await this.handleQuoteSuccessfulResponse(response);
        }
    }

    // Transient quote-service error (timeout/throttle): keep the card visible
    // and actionable rather than hiding a home that is likely available.
    handleTransientQuote() {
        if (this.bookBtnTarget) {
            this.bookBtnTarget.removeAttribute("disabled");
            this.bookBtnTarget.classList.remove('disabled');
            this.bookBtnTarget.textContent = "Book Now";
        }
    }

    async handleQuoteSuccessfulResponse(response) {
        const {total} = response;
        this.bookBtnTarget.classList.remove('disabled');
        this.bookBtnTarget.textContent = "Book Now";
        this.total = total;

        if (this.unitType === 'unit' || this.unitType === 'unit_type') {
            await this.handleProceedToBooking(this.currentEvent);
        }
    }

    withCoupon(path) {
        if (!this.coupon) return path;
        const [base, query = ""] = path.split("?");
        const sp = new URLSearchParams(query);
        sp.set("coupon", this.coupon);
        return `${base}?${sp.toString()}`;
    }

    async handleProceedToBooking(e) {
        e.preventDefault();
        const isUnitType = this.unitType === 'unit_type';

        if (e.target.dataset.suggestion) {
            // forwarded by glide bookings, no-op here
        }

        const cleanVillaPath = this.withCoupon(this.villaPath).replace(/^\/+/, '');
        const fullUrl = `${window.location.origin}/${cleanVillaPath}`;

        if (isUnitType) {
            window.location.href = fullUrl;
        } else {
            const exploreLink = this.element.querySelector(`.explore-${this.unitId}`);
            if (exploreLink) exploreLink.href = fullUrl;
            window.location.href = fullUrl;
        }
    }

    nightlyFromQuote(result) {
        if (!result || (result.status && result.status === "error")) return null;
        let nightly = result.nightly_rate;
        const promoActive = result.total_discount > 0 && result.num_nights > 0;
        const diffDays = Math.round(
            (new Date(this.checkin) - new Date()) / 86400000
        );
        // Skip the alternate-rate lookup when a promo is active: server-side
        // filters `additional_rate_types` down to the standard rate once the
        // promo is applied, so picking the min there overrides the rate that
        // TrackHS actually quoted and the subtraction lands on the wrong base.
        if (!promoActive && diffDays > parseInt(this.cicleLeadTime) && result.additional_rate_types) {
            nightly = Math.min(...result.additional_rate_types.map(i => i.nightly));
        }
        if (promoActive) {
            nightly = nightly - (result.total_discount / result.num_nights);
        }
        return nightly;
    }

    async fetchPrice() {
        if (!this.checkin) {
            this.minRateTargets.forEach(target => {
                target.classList.toggle("min-rate-loading");
                target.innerHTML = `${this.minRateBackup} / night`;
            });
            return;
        }

        this.bookBtnTarget.classList.add('disabled');
        this.bookBtnTarget.textContent = "Checking ...";

        const data = {
            unit_id: this.unitId,
            type: this.unitType,
            arrival_date: this.checkin,
            departure_date: this.checkout
        };

        let rackRate, couponRate, transient = false;

        if (this.coupon) {
            // Derive both numbers from a single coupon-applied quote so the
            // strike-through rack and the promo nightly share the same rate
            // basis (the rate TrackHS actually quoted with the promo).
            const result = await unitQuote({ ...data, promo_code: this.coupon });
            if (result && (!result.status || result.status !== "error")) {
                const numNights = parseInt(result.num_nights, 10) || 0;
                const nightly = parseFloat(result.nightly_rate) || 0;
                const totalDiscount = parseFloat(result.total_discount) || 0;
                rackRate = nightly;
                couponRate = totalDiscount > 0 && numNights > 0
                    ? nightly - (totalDiscount / numNights)
                    : null;
            } else {
                transient = !!(result && result.transient === true);
                rackRate = null;
                couponRate = null;
            }
        } else {
            const result = await unitQuote(data);
            if (result && result.status === 'error') transient = result.transient === true;
            rackRate = this.nightlyFromQuote(result);
            couponRate = null;
        }

        if (rackRate == null && couponRate == null) {
            // Only hide the home when TrackHS gave a definitive "unavailable".
            // On a transient failure keep the card and fall back to the backup price.
            if (transient) {
                this.handleTransientQuote();
            } else {
                this.disableBookButton();
            }
            this.minRateTargets.forEach(target => {
                target.classList.toggle("min-rate-loading");
                target.classList.toggle("min-rate-error");
                target.innerHTML = `${this.minRateBackup} / night`;
            });
            return;
        }

        const fmt = (n) => `$${parseFloat(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const couponApplied = couponRate != null && rackRate != null && couponRate < rackRate;

        this.minRateTargets.forEach(target => {
            target.classList.remove("min-rate-loading");
            if (couponApplied) {
                target.innerHTML =
                    `<span class="rate-rack">${fmt(rackRate)} / night</span>` +
                    `<span class="rate-coupon">${fmt(couponRate)} / night</span>`;
            } else {
                target.innerHTML = `${fmt(rackRate != null ? rackRate : couponRate)} / night`;
            }
        });
        this.bookBtnTarget.classList.remove('disabled');
        this.bookBtnTarget.textContent = "View Rates";
    }

    async observeVisibility() {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(async entry => {
                if (entry.isIntersecting) {
                    await this.fetchPrice();
                    observer.disconnect();
                }
            });
        });
        observer.observe(this.element);
    }

    disconnect() {
        if (this.glide) this.glide.destroy();
    }
}
